import React, {useContext, useEffect, useState} from "react";
import {AiModelPricingServiceContext, AppContextContext} from "../../Contexts";
import {Breadcrumb, Button, message, Modal, Space, Table, Tag} from "antd";
import {DeleteFilled, EditFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import * as globalStyles from "../App.module.css";
import AiModelPricing from "../../domain/AiModelPricing";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import {ColumnType, ColumnsType} from "antd/es/table";
import {formatAiExtractorType, OpenAiServiceTier} from "../../domain/AiExtractor";
import * as styles from "./AiModelPricingList.module.css";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const UNIT_PRICE_FORMATTER = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
});

const SERVICE_TIER_DISPLAY: Record<OpenAiServiceTier, { label: string; color?: string }> = {
    [OpenAiServiceTier.DEFAULT]: {label: "default"},
    [OpenAiServiceTier.FLEX]: {label: "flex", color: "blue"},
    [OpenAiServiceTier.PRIORITY]: {label: "priority", color: "purple"}
};

function AiModelPricingList() {
    const appContext = useContext(AppContextContext);
    const aiModelPricingService = useContext(AiModelPricingServiceContext);
    const navigate = useNavigate();
    const [tableStateId, setTableStateId] = useState(crypto.randomUUID());

    const title = "AI model credits";

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <p className={styles.description}>
                    Token prices are shown per 1 million tokens. Native web search is shown per 1,000 calls.
                </p>

                <AiModelPricingTable
                    key={tableStateId}
                    columns={getColumns}
                    toolbar={
                        <Button type={"primary"} icon={<PlusOutlined/>} onClick={() => navigate("/definitions/ai-model-pricing/add")}>
                            Add
                        </Button>
                    }
                />
            </>
        </DocumentTitle>
    );

    function getColumns(rows: AiModelPricing[]): ColumnsType<AiModelPricing> {
        const cols: ColumnsType<AiModelPricing> = [
            {
                dataIndex: "model",
                title: "Model",
                width: 190,
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                defaultSortOrder: "ascend",
                onCell: (_, index = 0) => ({rowSpan: groupRowSpan(rows, index)})
            },
            {
                dataIndex: "aiExtractorType",
                title: "Type",
                width: 150,
                render: value => formatAiExtractorType(value),
                onCell: (_, index = 0) => ({rowSpan: groupRowSpan(rows, index)})
            },
            {
                dataIndex: "openAiServiceTier",
                title: "Service tier",
                width: 110,
                render: renderServiceTier
            },
            priceColumn("inputPricePerMillionTokens", "Input"),
            priceColumn("cachedInputPricePerMillionTokens", "Cached"),
            priceColumn("cacheWriteInputPricePerMillionTokens", "Cache write", 115),
            priceColumn("outputPricePerMillionTokens", "Output"),
            {
                dataIndex: "webSearchPricePer1kCalls",
                title: "Web search",
                align: "right",
                width: 145,
                render: renderUnitPrice
            }
        ];

        cols.push({
            title: "Actions",
            width: 100,
            className: globalStyles["table-actions"],
            render: renderAction
        });

        return cols;
    }

    function renderAction(_: unknown, pricing: AiModelPricing) {
        const pricingLabel = `${pricing.model}, ${formatServiceTier(pricing.openAiServiceTier)} tier`;

        return <Space size="small">
            <Button
                icon={<EditFilled/>}
                className={"ant-btn-icon-only"}
                title="Edit"
                aria-label={`Edit credits for ${pricingLabel}`}
                onClick={() => navigate(`/definitions/ai-model-pricing/${pricing.id}`)}
            />
            <Button
                icon={<DeleteFilled/>}
                className={"ant-btn-icon-only"}
                title="Delete"
                aria-label={`Delete credits for ${pricingLabel}`}
                onClick={() => {
                    Modal.confirm({
                        content: <>Do you really want to delete credits for <b>{pricingLabel}</b>?</>,
                        okText: "Delete",
                        cancelText: "Cancel",
                        onOk: () => onDeleteConfirm(pricing)
                    });
                }}
            />
        </Space>;
    }

    function onDeleteConfirm(pricing: AiModelPricing) {
        aiModelPricingService.delete(pricing)
            .then(() => {
                message.success("AI model credits deleted.");
                setTableStateId(crypto.randomUUID());
            });
    }
}

export default AiModelPricingList;

interface TableProps {
    columns: (rows: AiModelPricing[]) => ColumnsType<AiModelPricing>;
    toolbar: React.ReactNode;
}

function AiModelPricingTable(props: TableProps) {
    const aiModelPricingService = useContext(AiModelPricingServiceContext);
    const [pricing, setPricing] = useState<PagedResult<AiModelPricing>>();

    const tableHandler = useTableHandler("model, aiExtractorType, openAiServiceTier", {
        reloadFunction: reload,
        persistentIdent: "AiModelPricingList",
    });

    useEffect(() => tableHandler.reload(), []);

    const rows = pricing?.data ?? [];

    return (
        <>
            <div className={globalStyles["common__top-button-bar"]}>
                <Space>
                    {props.toolbar}
                    <Button
                        className={"btn-seamless"}
                        icon={<ReloadOutlined/>}
                        title="Reload"
                        aria-label="Reload"
                        onClick={tableHandler.reload}
                    />
                </Space>
            </div>

            <Table
                dataSource={rows}
                loading={tableHandler.loading}
                pagination={{...tableHandler.pagination, placement: ["bottomRight"], hideOnSinglePage: true}}
                showSorterTooltip={false}
                rowKey="id"
                onChange={tableHandler.onTableChange}
                columns={props.columns(rows)}
                rowClassName={(record, index) => isModelGroupStart(rows, record, index) ? styles.modelGroupStart : ""}
                scroll={{x: 1080}}
                className={styles.table}
            />
        </>
    );

    function reload() {
        return aiModelPricingService.getList(tableHandler.queryOptions)
            .then(result => {
                tableHandler.updateTotal(result.total);
                setPricing(result);
            });
    }
}

function priceColumn(dataIndex: keyof AiModelPricing, title: string, width: number = 100): ColumnType<AiModelPricing> {
    return {
        dataIndex,
        title,
        align: "right",
        width,
        render: renderUnitPrice
    };
}

function renderUnitPrice(value?: number) {
    const formattedValue = typeof value === "number" ? UNIT_PRICE_FORMATTER.format(value) : "–";

    return <span className={styles.price}>{formattedValue}</span>;
}

function renderServiceTier(value?: OpenAiServiceTier) {
    if (!value) {
        return <span className={styles.noTier}>none</span>;
    }

    const display = SERVICE_TIER_DISPLAY[value];

    return <Tag color={display.color} bordered={false} className={styles.serviceTier}>{display.label}</Tag>;
}

function formatServiceTier(value?: OpenAiServiceTier) {
    return value ? SERVICE_TIER_DISPLAY[value].label : "none";
}

function groupRowSpan(rows: AiModelPricing[], index: number) {
    const groupKey = pricingGroupKey(rows[index]);

    if (index > 0 && pricingGroupKey(rows[index - 1]) === groupKey) {
        return 0;
    }

    let rowSpan = 1;

    while (index + rowSpan < rows.length && pricingGroupKey(rows[index + rowSpan]) === groupKey) {
        rowSpan++;
    }

    return rowSpan;
}

function isModelGroupStart(rows: AiModelPricing[], pricing: AiModelPricing, index: number) {
    return index > 0 && pricingGroupKey(rows[index - 1]) !== pricingGroupKey(pricing);
}

function pricingGroupKey(pricing: AiModelPricing) {
    return `${pricing.model}\u0000${pricing.aiExtractorType}`;
}
